import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import RejectIcon from "@mui/icons-material/CancelOutlined";
import SpamIcon from "@mui/icons-material/ReportGmailerrorredOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Stack,
  Tooltip,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { HTTPService } from "src/services/httpservice";
import { getHttpClient } from "src/services/rest-api";
import InitialsAvatar from "src/components/InitialsAvatar";
import StatusPill, { Tone } from "src/components/StatusPill";
import EmptyState from "src/components/EmptyState";
import InboxIcon from "@mui/icons-material/MoveToInboxOutlined";
import ContactDetailsDrawer from "./detailsDrawer";
import UpdateContactModal from "./UpdateContactModal";
import MoreIcon from "@mui/icons-material/MoreHoriz";

const PAGE_SIZE = 10;

const statusTone = (status: string): Tone => {
  switch (status) {
    case "NEW": return "info";
    case "CONTACTED": return "primary";
    case "QUALIFIED":
    case "RESCHEDULED": return "warning";
    case "WON":
    case "CONVERTED": return "success";
    case "LOST":
    case "SPAM":
    case "CLOSED": return "error";
    default: return "neutral";
  }
};

const ContactRequestsTable = ({ filters }: { filters: any }) => {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [editRow, setEditRow] = useState<any>(null);
  const [reviewAction, setReviewAction] = useState<"reject" | "spam" | null>(null);
  const [reviewRow, setReviewRow] = useState<any>(null);
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHttpClient(HTTPService.CONTACT, "get", null, { ...filters, page, limit: pageSize });
      setData(res.data.data);
      setCount(res.data.pagination.total || 0);
    } catch (e) {
      console.error("Fetch contacts error", e);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const update = useCallback(
    async (id: number, payload: any) => {
      try {
        await getHttpClient(`${HTTPService.CONTACT}/${id}`, "patch", payload);
        setData((prev) => prev.map((row) => (row.id === id ? { ...row, ...payload } : row)));
        if (selected?.id === id) setSelected((prev: any) => ({ ...prev, ...payload }));
      } catch (e) {
        console.error("Update contact error", e);
      }
    },
    [selected]
  );

  const handleToggleContacted = useCallback(
    (checked: boolean) => {
      if (!selected) return;
      update(selected.id, { contacted: checked, status: checked ? "CONTACTED" : "NEW" });
    },
    [selected, update]
  );

  const openReviewAction = (row: any, action: "reject" | "spam") => {
    setMenuAnchor(null);
    setMenuRow(null);
    setReviewRow(row);
    setReviewAction(action);
    setReviewMessage("");
  };

  const submitReviewAction = async () => {
    if (!reviewRow || !reviewAction) return;
    setReviewSaving(true);
    try {
      const res = await getHttpClient(`${HTTPService.CONTACT}/${reviewRow.id}/${reviewAction}`, "post", { message: reviewMessage });
      const next = res.data?.data || {
        ...reviewRow,
        status: reviewAction === "spam" ? "SPAM" : "LOST",
        remarks: reviewMessage || (reviewAction === "spam" ? "Marked as spam" : "Rejected by admin"),
      };
      setData((prev) => reviewAction === "spam" ? prev.filter((row) => row.id !== reviewRow.id) : prev.map((row) => (row.id === reviewRow.id ? next : row)));
      if (selected?.id === reviewRow.id) setSelected(next);
      setReviewAction(null);
      setReviewRow(null);
      setReviewMessage("");
    } catch (e) {
      console.error("Contact review action error", e);
    } finally {
      setReviewSaving(false);
    }
  };

  return (
    <>
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
      <Table
        sx={{
          minWidth: 900,
          "& th": {
            bgcolor: "#FBFCFE",
            fontSize: "11px",
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            borderBottom: "1px solid #EEF1F5",
            py: 1.25,
          },
          "& td": { borderBottom: "1px solid #F2F5F9", py: 1.25 },
          "& tbody tr:last-of-type td": { borderBottom: 0 },
          "& tbody tr": { transition: "background 120ms ease" },
          "& tbody tr:hover": { backgroundColor: "#FBFDFF" },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Contact</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Contacted</TableCell>
            <TableCell>Preferred Callback</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>

        <TableBody>
          {!loading && data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ border: 0 }}>
                <EmptyState icon={<InboxIcon />} title="No contact requests" description="Inbound leads from your website forms will show up here." />
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <InitialsAvatar name={row.name} size={32} />
                    <Box minWidth={0}>
                      <Typography fontSize="13.5px" fontWeight={700} lineHeight={1.3} noWrap>{row.name}</Typography>
                      <Typography fontSize="12px" color="text.secondary" noWrap>{row.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography fontSize="13px" fontWeight={600}>{row.company || "—"}</Typography>
                  <Typography fontSize="12px" color="text.secondary">{row.country || ""}</Typography>
                </TableCell>

                <TableCell>
                  <StatusPill label={row.status} tone={statusTone(row.status)} />
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Switch
                      size="small"
                      checked={row.contacted}
                      onChange={(e) => update(row.id, { contacted: e.target.checked, status: e.target.checked ? "CONTACTED" : "NEW" })}
                    />
                    <Typography fontSize="12px" color="text.secondary">{row.contacted ? "Yes" : "No"}</Typography>
                  </Stack>
                </TableCell>

                <TableCell>
                  {row.preferred_contact_date ? (
                    <Box>
                      <Typography fontSize="13px" fontWeight={600}>
                        {new Date(row.preferred_contact_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </Typography>
                      {row.preferred_contact_time && (
                        <Typography fontSize="12px" color="text.secondary">
                          {row.preferred_contact_time}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography fontSize="13px" color="text.secondary">—</Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Typography fontSize="13px" color="text.secondary">{new Date(row.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</Typography>
                </TableCell>

                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => setSelected(row)} sx={{ width: 30, height: 30, borderRadius: "8px", color: "text.secondary", "&:hover": { bgcolor: "#F1F5FF", color: "primary.main" } }}>
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuRow(row);
                        }}
                        sx={{ width: 30, height: 30, borderRadius: "8px", color: "text.secondary", "&:hover": { bgcolor: "#F8FAFC" } }}
                      >
                        <MoreIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="flex-end"
        px={1.5}
        py={0.5}
        borderTop="1px solid #EEF2F6"
        sx={{
          "& .MuiTablePagination-toolbar": {
            px: { xs: 0, sm: 1 },
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "flex-end" },
            rowGap: 0.5,
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: 12,
            color: "text.secondary",
          },
        }}
      >
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={count}
          rowsPerPage={pageSize}
          page={page - 1}
          onPageChange={(_, nextPage) => setPage(nextPage + 1)}
          onRowsPerPageChange={(e) => {
            setPage(1);
            setPageSize(parseInt(e.target.value, 10));
          }}
        />
      </Box>

      <ContactDetailsDrawer open={Boolean(selected)} onClose={() => setSelected(null)} data={selected} onToggleContacted={handleToggleContacted} />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuRow(null);
        }}
        PaperProps={{ sx: { borderRadius: "10px", boxShadow: "0 10px 24px rgba(15,23,42,0.12)", border: "1px solid #EEF2F6", minWidth: 170 } }}
      >
        <MenuItem onClick={() => { setEditRow(menuRow); setMenuAnchor(null); }} sx={{ fontSize: 13, gap: 1 }}>
          <EditIcon sx={{ fontSize: 17, color: "text.secondary" }} /> Update lead
        </MenuItem>
        <MenuItem disabled={menuRow?.status === "SPAM"} onClick={() => openReviewAction(menuRow, "reject")} sx={{ fontSize: 13, gap: 1 }}>
          <RejectIcon sx={{ fontSize: 17, color: "error.main" }} /> Reject
        </MenuItem>
        <MenuItem disabled={menuRow?.status === "SPAM"} onClick={() => openReviewAction(menuRow, "spam")} sx={{ fontSize: 13, gap: 1, color: "error.main" }}>
          <SpamIcon sx={{ fontSize: 17 }} /> Mark as spam
        </MenuItem>
      </Menu>

      <UpdateContactModal
        open={Boolean(editRow)}
        data={editRow}
        onClose={() => setEditRow(null)}
        onSave={async (payload) => {
          await update(editRow.id, payload);
          setEditRow(null);
        }}
      />

      <Dialog open={Boolean(reviewAction)} onClose={() => setReviewAction(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 750 }}>{reviewAction === "spam" ? "Mark Contact as Spam" : "Reject Contact Request"}</DialogTitle>
        <DialogContent>
          <Typography fontSize="13px" color="text.secondary" mb={2}>
            {reviewRow?.name} · {reviewRow?.email}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label={reviewAction === "spam" ? "Internal spam note" : "Message to send by mail"}
            value={reviewMessage}
            onChange={(e) => setReviewMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={reviewSaving} onClick={() => setReviewAction(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={reviewSaving} onClick={submitReviewAction}>
            {reviewSaving ? "Saving..." : reviewAction === "spam" ? "Mark Spam" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactRequestsTable;
